import { useState, useEffect, useCallback } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { format } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import {
  Lock,
  Mountain,
  LogOut,
  Users,
  DollarSign,
  TrendingUp,
  CalendarDays,
  Clock,
  XCircle,
  CheckCircle,
  AlertCircle,
  Loader2,
  Search,
  Settings,
  List,
  Video,
  Shield,
  Percent,
  Sparkles,
  Trash2,
  PlusCircle,
  FileDown,
  Bot,
  Copy,
  Send,
  RotateCcw,
  Eye,
  EyeOff,
  Ban,
  User,
  BarChart3,
} from "lucide-react";
import { useLang } from "@/i18n";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOUR_LABELS = {
  single: "Singolo",
  double: "Doppio",
  mot_single: "Mottarone (S)",
  mot_double: "Mottarone (D)",
};
const STATUS_CONFIG = {
  confirmed: { label: "Confermato", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
  pending: { label: "In attesa", color: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
  cancelled: { label: "Cancellato", color: "bg-red-500/20 text-red-400 border-red-500/30" },
};

/* ─── LOGIN SCREEN ─── */
function LoginScreen({ onLogin }) {
  const { t, lang } = useLang();
  const isIt = lang === "it";
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatusMsg("");
    for (let i = 0; i < 3; i++) {
      try {
        const res = await axios.post(`${API}/admin/login`, { password }, { timeout: 45000 });
        localStorage.setItem("admin_token", res.data.token);
        onLogin(res.data.token);
        setLoading(false);
        return;
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error(t("wrongPassword"));
          setLoading(false);
          setStatusMsg("");
          return;
        }
        if (i < 2) {
          setStatusMsg(isIt
            ? `Server in avvio, nuovo tentativo (${i + 2}/3)...`
            : `Server starting, retrying (${i + 2}/3)...`);
          await new Promise((r) => setTimeout(r, 5000));
        }
      }
    }
    toast.error(isIt
      ? "Server non raggiungibile. Attendi 30 secondi e riprova."
      : "Server unreachable. Wait 30 seconds and try again.");
    setStatusMsg("");
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a1a0f] flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="admin-login-form">
        <div className="flex items-center gap-2 justify-center mb-8">
          <Mountain className="w-5 h-5 text-[#2d7a4a]" />
          <span className="font-bold text-[#e8f0e8] uppercase text-sm tracking-wider">
            {t("adminTitle")}
          </span>
        </div>
        <div className="glass-card rounded-xl p-8">
          <div className="w-14 h-14 bg-[#2d7a4a]/10 border border-[#2d7a4a]/30 rounded-xl flex items-center justify-center mx-auto mb-6">
            <Lock className="w-6 h-6 text-[#2d7a4a]" />
          </div>
          <h1 className="text-2xl font-bold uppercase text-center text-[#e8f0e8] mb-6" data-testid="admin-access-title">
            {t("adminAccess")}
          </h1>
          <div className="relative mb-4">
            <Input
              data-testid="admin-password-input"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t("password")}
              className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] focus:border-[#2d7a4a] rounded-lg h-12 pr-12 placeholder:text-[#3a5a3a]"
            />
            <button
              type="button"
              data-testid="toggle-password-visibility"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Nascondi password" : "Mostra password"}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8faa8f] hover:text-[#d4a056] transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading || !password}
            className="w-full bg-[#2d7a4a] text-white font-bold uppercase tracking-wider py-3 hover:bg-[#3a9960] transition-all text-sm disabled:opacity-50 rounded-lg"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("enter")}
          </button>
          {statusMsg && (
            <p data-testid="login-status-msg" className="text-xs text-[#d4a056] text-center mt-3">
              {statusMsg}
            </p>
          )}
        </div>
      </form>
    </div>
  );
}

/* ─── STAT CARD ─── */
function StatCard({ icon: Icon, label, value, accent, gold }) {
  return (
    <div className="glass-card rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${gold ? "text-[#d4a056]" : accent ? "text-[#2d7a4a]" : "text-[#8faa8f]"}`} />
        <span className="text-xs uppercase tracking-wider text-[#8faa8f]">{label}</span>
      </div>
      <span className={`text-2xl font-bold ${gold ? "text-[#d4a056]" : accent ? "text-[#2d7a4a]" : "text-[#e8f0e8]"}`}>
        {value}
      </span>
    </div>
  );
}

/* ─── BOOKINGS TAB ─── */
function BookingsTab({ token }) {
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/bookings?`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterDate) url += `date=${filterDate}&`;
      const res = await axios.get(url, { headers });
      setBookings(res.data);
    } catch {
      toast.error(t("errorLoadBookings"));
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterDate, token]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${id}/status`, { status: newStatus }, { headers });
      toast.success(`${t("statusUpdated")}: ${newStatus}`);
      fetchBookings();
    } catch {
      toast.error(t("errorUpdateStatus"));
    }
  };

  const filtered = bookings.filter((b) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      b.customer_name?.toLowerCase().includes(s) ||
      b.customer_email?.toLowerCase().includes(s) ||
      b.customer_phone?.includes(s)
    );
  });

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#3a5a3a]" />
          <Input
            data-testid="search-bookings"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("searchClient")}
            className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-10 pl-10 placeholder:text-[#3a5a3a]"
          />
        </div>
        <select
          data-testid="filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="bg-[#0f2518] border border-[#1a4a2e] text-[#e8f0e8] h-10 px-3 text-sm focus:border-[#2d7a4a] outline-none rounded-lg"
        >
          <option value="">{t("allStatuses")}</option>
          <option value="confirmed">{t("statusConfirmed")}</option>
          <option value="pending">{t("statusPending")}</option>
          <option value="cancelled">{t("statusCancelled")}</option>
        </select>
        <Input
          data-testid="filter-date"
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-10 w-auto placeholder:text-[#3a5a3a]"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 text-[#2d7a4a] animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-[#8faa8f]">{t("noBookings")}</div>
      ) : (
        <div className="overflow-x-auto glass-card rounded-xl">
          <table className="w-full text-sm" data-testid="bookings-table">
            <thead>
              <tr className="bg-[#0f2518] text-[#8faa8f] uppercase text-xs tracking-wider">
                <th className="text-left p-3">{t("colDate")}</th>
                <th className="text-left p-3">{t("colTime")}</th>
                <th className="text-left p-3">{t("colTour")}</th>
                <th className="text-left p-3">{t("colClient")}</th>
                <th className="text-left p-3">{t("colContact")}</th>
                <th className="text-right p-3">{t("colPrice")}</th>
                <th className="text-right p-3">{t("colFee")}</th>
                <th className="text-right p-3">{t("colNet")}</th>
                <th className="text-center p-3">{t("colStatus")}</th>
                <th className="text-center p-3">{t("colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => {
                const st = STATUS_CONFIG[b.status] || STATUS_CONFIG.pending;
                return (
                  <tr
                    key={b.id}
                    data-testid={`booking-row-${i}`}
                    className={`border-t border-[#1a4a2e]/50 ${i % 2 === 0 ? "bg-[#0a1a0f]/50" : "bg-[#0f2518]/50"} hover:bg-[#1a2e20] transition-colors`}
                  >
                    <td className="p-3 text-[#e8f0e8] whitespace-nowrap">{b.booking_date}</td>
                    <td className="p-3 text-[#e8f0e8]">{b.time_slot}</td>
                    <td className="p-3">
                      <span className="text-[#e8f0e8]">{TOUR_LABELS[b.tour_type] || b.tour_type}</span>
                      {b.include_video && <Video className="w-3 h-3 text-[#d4a056] inline ml-1.5" />}
                      {b.license_b_confirmed && <Shield className="w-3 h-3 text-[#2d7a4a] inline ml-1" />}
                    </td>
                    <td className="p-3 text-[#e8f0e8]">{b.customer_name}</td>
                    <td className="p-3">
                      <div className="text-[#8faa8f] text-xs">{b.customer_email}</div>
                      <div className="text-[#3a5a3a] text-xs">{b.customer_phone}</div>
                    </td>
                    <td className="p-3 text-right text-[#d4a056] font-bold">EUR {b.total_price}</td>
                    <td className="p-3 text-right text-[#8faa8f] text-xs">EUR {b.management_fee || 0}</td>
                    <td className="p-3 text-right text-[#2d7a4a] font-bold text-xs">EUR {b.net_amount || 0}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-block text-xs px-2.5 py-1 border font-bold uppercase tracking-wider rounded ${st.color}`}>
                        {st.label}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {b.status !== "confirmed" && (
                          <button
                            data-testid={`confirm-btn-${i}`}
                            onClick={() => updateStatus(b.id, "confirmed")}
                            title="Conferma"
                            className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 transition-colors rounded"
                          >
                            <CheckCircle className="w-4 h-4" />
                          </button>
                        )}
                        {b.status !== "cancelled" && (
                          <button
                            data-testid={`cancel-btn-${i}`}
                            onClick={() => updateStatus(b.id, "cancelled")}
                            title="Cancella"
                            className="p-1.5 text-red-400 hover:bg-red-500/10 transition-colors rounded"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3 text-xs text-[#3a5a3a]">{filtered.length} {t("bookings")}</div>
    </div>
  );
}

/* ─── SLOTS TAB ─── */
function SlotsTab({ token }) {
  const { t, lang } = useLang();
  const [date, setDate] = useState(undefined);
  const [overrides, setOverrides] = useState([]);
  const [slotForms, setSlotForms] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState("");

  const headers = { Authorization: `Bearer ${token}` };
  const TIME_SLOTS = ["09:00", "14:00", "18:00"];

  const fetchOverrides = useCallback(async (d) => {
    setLoading(true);
    try {
      const dateStr = format(d, "yyyy-MM-dd");
      const res = await axios.get(`${API}/admin/slot-overrides?date=${dateStr}`, { headers });
      setOverrides(res.data);
      const forms = {};
      TIME_SLOTS.forEach((ts) => {
        const ov = res.data.find((o) => o.time_slot === ts);
        forms[ts] = {
          max_quads: ov ? ov.max_quads : 7,
          is_closed: ov ? ov.is_closed : false,
          hasOverride: !!ov,
        };
      });
      setSlotForms(forms);
    } catch {
      const forms = {};
      TIME_SLOTS.forEach((ts) => { forms[ts] = { max_quads: 7, is_closed: false, hasOverride: false }; });
      setSlotForms(forms);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (date) fetchOverrides(date);
  }, [date, fetchOverrides]);

  const saveOverride = async (ts) => {
    if (!date) return;
    setSaving(ts);
    const f = slotForms[ts];
    try {
      await axios.post(`${API}/admin/slot-overrides`, {
        override_date: format(date, "yyyy-MM-dd"),
        time_slot: ts,
        max_quads: f.max_quads,
        is_closed: f.is_closed,
      }, { headers });
      toast.success(`${t("slotUpdated")} ${ts}`);
      fetchOverrides(date);
    } catch {
      toast.error(t("errorSave"));
    } finally {
      setSaving("");
    }
  };

  const resetOverride = async (ts) => {
    if (!date) return;
    setSaving(ts);
    try {
      await axios.delete(`${API}/admin/slot-overrides?date=${format(date, "yyyy-MM-dd")}&time_slot=${ts}`, { headers });
      toast.success(`${t("slotReset")} ${ts}`);
      fetchOverrides(date);
    } catch {
      toast.error(t("errorReset"));
    } finally {
      setSaving("");
    }
  };

  const calendarLocale = lang === "it" ? itLocale : undefined;

  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div>
        <label className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a] mb-4 block">
          {t("step1")}
        </label>
        <div className="glass-card rounded-xl p-4 inline-block" data-testid="admin-calendar">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            locale={calendarLocale}
            className="text-[#e8f0e8]"
            classNames={{
              day_selected: "bg-[#2d7a4a] text-white hover:bg-[#3a9960] hover:text-white focus:bg-[#2d7a4a] focus:text-white",
              day_today: "bg-[#1a2e20] text-[#2d7a4a] font-bold",
              day: "h-9 w-9 p-0 font-normal text-[#e8f0e8] hover:bg-[#1a2e20] rounded-md",
              head_cell: "text-[#8faa8f] font-normal text-[0.8rem] w-9",
              caption_label: "text-sm font-bold text-[#e8f0e8] uppercase",
              nav_button: "border border-[#1a4a2e] text-[#8faa8f] hover:bg-[#1a2e20] hover:text-[#2d7a4a] h-7 w-7 bg-transparent p-0 rounded-md",
              cell: "text-center text-sm p-0 relative",
              day_outside: "text-[#1a4a2e]",
              day_disabled: "text-[#1a4a2e] opacity-50",
            }}
          />
        </div>
        {date && (
          <p className="mt-4 text-[#8faa8f] text-sm">
            {t("configFor")} <span className="text-[#d4a056] font-bold">{format(date, "d MMMM yyyy", { locale: calendarLocale })}</span>
          </p>
        )}
      </div>

      <div>
        {!date ? (
          <div className="flex items-center justify-center h-full text-[#8faa8f]">
            <CalendarDays className="w-5 h-5 mr-2" /> {t("selectDateConfig")}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-[#2d7a4a] animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <label className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a] mb-2 block">
              {t("timeSlots")}
            </label>
            {TIME_SLOTS.map((ts) => {
              const f = slotForms[ts] || { max_quads: 7, is_closed: false, hasOverride: false };
              return (
                <div
                  key={ts}
                  data-testid={`slot-config-${ts.replace(":", "")}`}
                  className={`glass-card rounded-xl p-4 ${f.is_closed ? "border-red-500/30 border" : f.hasOverride ? "border-[#2d7a4a]/40 border" : ""}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className={`w-4 h-4 ${f.is_closed ? "text-red-400" : "text-[#2d7a4a]"}`} />
                      <span className="font-bold text-lg text-[#e8f0e8]">{ts}</span>
                      {f.hasOverride && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-[#2d7a4a]/10 text-[#2d7a4a] border border-[#2d7a4a]/30 uppercase font-bold rounded">
                          {t("override")}
                        </span>
                      )}
                    </div>
                    {f.is_closed && (
                      <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 border border-red-500/30 uppercase font-bold rounded">
                        {t("closed")}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#8faa8f] uppercase">{t("maxQuad")}</span>
                      <Input
                        type="number"
                        min="0"
                        max="20"
                        value={f.max_quads}
                        disabled={f.is_closed}
                        onChange={(e) =>
                          setSlotForms((prev) => ({
                            ...prev,
                            [ts]: { ...prev[ts], max_quads: parseInt(e.target.value) || 0 },
                          }))
                        }
                        className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-9 w-20 text-center disabled:opacity-30"
                      />
                    </div>

                    <button
                      onClick={() =>
                        setSlotForms((prev) => ({
                          ...prev,
                          [ts]: { ...prev[ts], is_closed: !prev[ts].is_closed },
                        }))
                      }
                      className={`text-xs font-bold uppercase px-3 py-2 border transition-all rounded-lg ${
                        f.is_closed
                          ? "bg-red-500/20 border-red-500/30 text-red-400"
                          : "bg-transparent border-[#1a4a2e] text-[#8faa8f] hover:border-red-500/30 hover:text-red-400"
                      }`}
                    >
                      {f.is_closed ? t("reopen") : t("close")}
                    </button>

                    <button
                      data-testid={`save-slot-${ts.replace(":", "")}`}
                      onClick={() => saveOverride(ts)}
                      disabled={saving === ts}
                      className="bg-[#2d7a4a] text-white font-bold uppercase px-4 py-2 text-xs hover:bg-[#3a9960] transition-all disabled:opacity-50 ml-auto rounded-lg"
                    >
                      {saving === ts ? <Loader2 className="w-3 h-3 animate-spin" /> : t("save")}
                    </button>

                    {f.hasOverride && (
                      <button
                        onClick={() => resetOverride(ts)}
                        className="text-xs text-[#8faa8f] hover:text-[#d4a056] transition-colors uppercase"
                      >
                        {t("reset")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── REPORT PDF BAR ─── */
function ReportBar({ token }) {
  const { lang } = useLang();
  const isIt = lang === "it";
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const firstOfMonth = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
  const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(today);
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!start || !end) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/admin/report/pdf?start=${start}&end=${end}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `report_spiritolibero_${start}_${end}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isIt ? "Report PDF scaricato" : "PDF report downloaded");
    } catch {
      toast.error(isIt ? "Errore generazione report" : "Report generation error");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="glass-card rounded-xl p-4 mb-8 flex flex-wrap items-end gap-3" data-testid="report-pdf-bar">
      <div className="flex items-center gap-2 mr-2">
        <FileDown className="w-4 h-4 text-[#d4a056]" />
        <span className="text-xs font-bold uppercase tracking-widest text-[#d4a056]">
          {isIt ? "Report PDF" : "PDF Report"}
        </span>
      </div>
      <div>
        <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Dal" : "From"}</span>
        <Input
          data-testid="report-start-date"
          type="date"
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-9 w-auto"
        />
      </div>
      <div>
        <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Al" : "To"}</span>
        <Input
          data-testid="report-end-date"
          type="date"
          value={end}
          onChange={(e) => setEnd(e.target.value)}
          className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-9 w-auto"
        />
      </div>
      <button
        data-testid="download-report-btn"
        onClick={download}
        disabled={downloading || !start || !end}
        className="bg-[#d4a056] text-[#0a1a0f] font-bold uppercase px-5 py-2 text-xs hover:bg-[#e0b86e] transition-all disabled:opacity-50 rounded-lg flex items-center gap-2 h-9"
      >
        {downloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
        {isIt ? "SCARICA REPORT" : "DOWNLOAD REPORT"}
      </button>
      <span className="text-[10px] text-[#5a7a5a] w-full sm:w-auto">
        {isIt ? "Lordo, Fee 10% e Netto delle prenotazioni confermate nel periodo" : "Gross, 10% Fee and Net of confirmed bookings in the period"}
      </span>
    </div>
  );
}

/* ─── MOTTARONE TAB ─── */
function MottaroneTab({ token }) {
  const { lang } = useLang();
  const isIt = lang === "it";
  const [dates, setDates] = useState([]);
  const [settings, setSettings] = useState({ description_it: "", description_en: "" });
  const [loading, setLoading] = useState(true);
  const [savingDesc, setSavingDesc] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const emptyForm = { tour_date: "", time_slot: "09:00", price_single: 200, price_double: 280, max_quads: 7, duration: "5 ore" };
  const [newDate, setNewDate] = useState(emptyForm);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/mottarone/dates`, { headers }),
        axios.get(`${API}/admin/mottarone/settings`, { headers }),
      ]);
      setDates(Array.isArray(dRes.data) ? dRes.data : []);
      if (sRes.data && typeof sRes.data === "object") setSettings(sRes.data);
    } catch {
      toast.error(isIt ? "Errore caricamento dati Mottarone" : "Error loading Mottarone data");
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSettings = async () => {
    setSavingDesc(true);
    try {
      await axios.put(`${API}/admin/mottarone/settings`, settings, { headers });
      toast.success(isIt ? "Descrizione aggiornata" : "Description updated");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (isIt ? "Errore salvataggio" : "Save error"));
    } finally {
      setSavingDesc(false);
    }
  };

  const saveDate = async (payload, key) => {
    setSavingKey(key);
    try {
      await axios.post(`${API}/admin/mottarone/dates`, {
        tour_date: payload.tour_date,
        time_slot: payload.time_slot,
        price_single: parseFloat(payload.price_single) || 0,
        price_double: parseFloat(payload.price_double) || 0,
        max_quads: parseInt(payload.max_quads) || 0,
        duration: payload.duration || "",
        is_active: payload.is_active !== false,
      }, { headers });
      toast.success(isIt ? "Data Mottarone salvata" : "Mottarone date saved");
      if (key === "new") setNewDate(emptyForm);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (isIt ? "Errore salvataggio" : "Save error"));
    } finally {
      setSavingKey("");
    }
  };

  const deleteDate = async (d) => {
    setSavingKey(d.tour_date);
    try {
      await axios.delete(`${API}/admin/mottarone/dates?date=${d.tour_date}`, { headers });
      toast.success(isIt ? "Data rimossa" : "Date removed");
      fetchAll();
    } catch {
      toast.error(isIt ? "Errore rimozione" : "Delete error");
    } finally {
      setSavingKey("");
    }
  };

  const updateRow = (idx, field, value) => {
    setDates((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const fieldCls = "bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-9";

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#d4a056] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Descrizione */}
      <div className="glass-card rounded-xl p-6 border border-[#d4a056]/30" data-testid="mottarone-settings-card">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#d4a056]" />
          <h3 className="font-bold uppercase text-sm tracking-widest text-[#d4a056]">
            {isIt ? "Descrizione Tour Mottarone" : "Mottarone Tour Description"}
          </h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <span className="text-xs text-[#8faa8f] uppercase tracking-wider block mb-1.5">Italiano</span>
            <textarea
              data-testid="mottarone-desc-it"
              value={settings.description_it || ""}
              onChange={(e) => setSettings((s) => ({ ...s, description_it: e.target.value }))}
              rows={3}
              maxLength={255}
              className="w-full bg-[#0f2518] border border-[#1a4a2e] text-[#e8f0e8] rounded-lg p-3 text-sm focus:border-[#d4a056] outline-none resize-y"
            />
          </div>
          <div>
            <span className="text-xs text-[#8faa8f] uppercase tracking-wider block mb-1.5">English</span>
            <textarea
              data-testid="mottarone-desc-en"
              value={settings.description_en || ""}
              onChange={(e) => setSettings((s) => ({ ...s, description_en: e.target.value }))}
              rows={3}
              maxLength={255}
              className="w-full bg-[#0f2518] border border-[#1a4a2e] text-[#e8f0e8] rounded-lg p-3 text-sm focus:border-[#d4a056] outline-none resize-y"
            />
          </div>
        </div>
        <button
          data-testid="save-mottarone-desc-btn"
          onClick={saveSettings}
          disabled={savingDesc}
          className="bg-[#d4a056] text-[#0a1a0f] font-bold uppercase px-5 py-2 text-xs hover:bg-[#e0b86e] transition-all disabled:opacity-50 rounded-lg"
        >
          {savingDesc ? <Loader2 className="w-3 h-3 animate-spin" /> : (isIt ? "SALVA DESCRIZIONE" : "SAVE DESCRIPTION")}
        </button>
      </div>

      {/* Nuova data */}
      <div className="glass-card rounded-xl p-6" data-testid="mottarone-add-date-card">
        <div className="flex items-center gap-2 mb-4">
          <PlusCircle className="w-4 h-4 text-[#2d7a4a]" />
          <h3 className="font-bold uppercase text-sm tracking-widest text-[#2d7a4a]">
            {isIt ? "Aggiungi Data Mottarone" : "Add Mottarone Date"}
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
          <div>
            <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Data" : "Date"}</span>
            <Input data-testid="new-mottarone-date" type="date" value={newDate.tour_date}
              onChange={(e) => setNewDate((f) => ({ ...f, tour_date: e.target.value }))} className={fieldCls} />
          </div>
          <div>
            <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Orario" : "Time"}</span>
            <Input data-testid="new-mottarone-time" type="time" value={newDate.time_slot}
              onChange={(e) => setNewDate((f) => ({ ...f, time_slot: e.target.value }))} className={fieldCls} />
          </div>
          <div>
            <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">€ {isIt ? "Singolo" : "Single"}</span>
            <Input data-testid="new-mottarone-price-single" type="number" min="1" value={newDate.price_single}
              onChange={(e) => setNewDate((f) => ({ ...f, price_single: e.target.value }))} className={fieldCls} />
          </div>
          <div>
            <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">€ {isIt ? "Doppio" : "Double"}</span>
            <Input data-testid="new-mottarone-price-double" type="number" min="1" value={newDate.price_double}
              onChange={(e) => setNewDate((f) => ({ ...f, price_double: e.target.value }))} className={fieldCls} />
          </div>
          <div>
            <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">Max Quad</span>
            <Input data-testid="new-mottarone-max-quads" type="number" min="1" max="20" value={newDate.max_quads}
              onChange={(e) => setNewDate((f) => ({ ...f, max_quads: e.target.value }))} className={fieldCls} />
          </div>
          <div>
            <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Durata" : "Duration"}</span>
            <Input data-testid="new-mottarone-duration" type="text" placeholder="5 ore" value={newDate.duration}
              onChange={(e) => setNewDate((f) => ({ ...f, duration: e.target.value }))} className={fieldCls} />
          </div>
        </div>
        <button
          data-testid="add-mottarone-date-btn"
          onClick={() => saveDate({ ...newDate, is_active: true }, "new")}
          disabled={!newDate.tour_date || savingKey === "new"}
          className="mt-4 bg-[#2d7a4a] text-white font-bold uppercase px-5 py-2 text-xs hover:bg-[#3a9960] transition-all disabled:opacity-50 rounded-lg"
        >
          {savingKey === "new" ? <Loader2 className="w-3 h-3 animate-spin" /> : (isIt ? "AGGIUNGI DATA" : "ADD DATE")}
        </button>
      </div>

      {/* Date esistenti */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="w-4 h-4 text-[#d4a056]" />
          <h3 className="font-bold uppercase text-sm tracking-widest text-[#d4a056]">
            {isIt ? "Date Programmate" : "Scheduled Dates"} ({dates.length})
          </h3>
        </div>
        {dates.length === 0 ? (
          <p className="text-[#8faa8f] text-sm" data-testid="mottarone-no-dates-admin">
            {isIt ? "Nessuna data Mottarone programmata." : "No Mottarone dates scheduled."}
          </p>
        ) : (
          <div className="space-y-3">
            {dates.map((d, i) => (
              <div
                key={d.id || d.tour_date}
                data-testid={`mottarone-date-row-${i}`}
                className={`glass-card rounded-xl p-4 ${d.is_active === false ? "border border-red-500/30 opacity-70" : "border border-[#d4a056]/20"}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[#e8f0e8]">{d.tour_date}</span>
                    <span className="text-xs text-[#8faa8f]">
                      {isIt ? "Prenotati" : "Booked"}: <span className="text-[#d4a056] font-bold">{d.booked_quads || 0}</span> / {d.max_quads}
                    </span>
                    {d.is_active === false && (
                      <span className="text-[10px] px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 uppercase font-bold rounded">
                        {isIt ? "Disattivata" : "Inactive"}
                      </span>
                    )}
                  </div>
                  <button
                    data-testid={`delete-mottarone-row-${i}`}
                    onClick={() => deleteDate(d)}
                    disabled={savingKey === d.tour_date}
                    className="p-1.5 text-red-400 hover:bg-red-500/10 transition-colors rounded"
                    title={isIt ? "Elimina data" : "Delete date"}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 items-end">
                  <div>
                    <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Orario" : "Time"}</span>
                    <Input type="time" value={(d.time_slot || "09:00").slice(0, 5)}
                      onChange={(e) => updateRow(i, "time_slot", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">€ {isIt ? "Singolo" : "Single"}</span>
                    <Input type="number" min="1" value={d.price_single}
                      onChange={(e) => updateRow(i, "price_single", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">€ {isIt ? "Doppio" : "Double"}</span>
                    <Input type="number" min="1" value={d.price_double}
                      onChange={(e) => updateRow(i, "price_double", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">Max Quad</span>
                    <Input type="number" min="1" max="20" value={d.max_quads}
                      onChange={(e) => updateRow(i, "max_quads", e.target.value)} className={fieldCls} />
                  </div>
                  <div>
                    <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Durata" : "Duration"}</span>
                    <Input type="text" value={d.duration || ""}
                      onChange={(e) => updateRow(i, "duration", e.target.value)} className={fieldCls} />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => updateRow(i, "is_active", d.is_active === false)}
                      className={`text-[10px] font-bold uppercase px-2.5 py-2 border transition-all rounded-lg ${
                        d.is_active === false
                          ? "bg-red-500/20 border-red-500/30 text-red-400"
                          : "bg-transparent border-[#1a4a2e] text-[#8faa8f] hover:border-red-500/30 hover:text-red-400"
                      }`}
                    >
                      {d.is_active === false ? (isIt ? "RIATTIVA" : "ENABLE") : (isIt ? "DISATTIVA" : "DISABLE")}
                    </button>
                    <button
                      data-testid={`save-mottarone-row-${i}`}
                      onClick={() => saveDate(d, d.tour_date)}
                      disabled={savingKey === d.tour_date}
                      className="bg-[#d4a056] text-[#0a1a0f] font-bold uppercase px-4 py-2 text-xs hover:bg-[#e0b86e] transition-all disabled:opacity-50 rounded-lg"
                    >
                      {savingKey === d.tour_date ? <Loader2 className="w-3 h-3 animate-spin" /> : (isIt ? "SALVA" : "SAVE")}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── AI ASSISTANT TAB (Claude Sonnet 4.6) ─── */
function AiTab({ token }) {
  const { lang } = useLang();
  const isIt = lang === "it";
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);

  const quickActions = [
    { label: isIt ? "Risposta a recensione" : "Review reply", prompt: isIt ? "Scrivi una risposta calorosa a questa recensione Google di un cliente: \"\"" : "Write a warm reply to this Google review from a customer: \"\"" },
    { label: isIt ? "Email cliente" : "Customer email", prompt: isIt ? "Scrivi un'email a un cliente per: " : "Write an email to a customer about: " },
    { label: isIt ? "Post Instagram" : "Instagram post", prompt: isIt ? "Scrivi un post Instagram coinvolgente (con hashtag) su: " : "Write an engaging Instagram post (with hashtags) about: " },
    { label: isIt ? "Descrizione tour" : "Tour description", prompt: isIt ? "Scrivi una breve descrizione d'effetto per il tour: " : "Write a short catchy description for the tour: " },
  ];

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setStreaming(true);
    setMessages((prev) => [...prev, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/admin/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let gotError = false;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          let d;
          try { d = JSON.parse(part.slice(6)); } catch { continue; }
          if (d.delta) {
            setMessages((prev) => {
              const next = [...prev];
              next[next.length - 1] = { role: "assistant", content: next[next.length - 1].content + d.delta };
              return next;
            });
          } else if (d.error) {
            gotError = true;
            toast.error(d.error);
          }
        }
      }
      if (gotError) {
        setMessages((prev) => prev.slice(0, -2));
      }
    } catch {
      toast.error(isIt ? "Errore assistente AI, riprova." : "AI assistant error, please retry.");
      setMessages((prev) => prev.slice(0, -2));
    } finally {
      setStreaming(false);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(isIt ? "Testo copiato" : "Text copied");
    } catch {
      toast.error(isIt ? "Copia non riuscita" : "Copy failed");
    }
  };

  const resetChat = () => {
    setSessionId(crypto.randomUUID());
    setMessages([]);
    setInput("");
  };

  return (
    <div className="max-w-3xl" data-testid="ai-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bot className="w-4 h-4 text-[#d4a056]" />
          <h3 className="font-bold uppercase text-sm tracking-widest text-[#d4a056]">
            {isIt ? "Assistente AI (Claude)" : "AI Assistant (Claude)"}
          </h3>
        </div>
        <button
          data-testid="ai-reset-btn"
          onClick={resetChat}
          className="flex items-center gap-1.5 text-xs text-[#8faa8f] hover:text-[#d4a056] transition-colors uppercase font-bold"
        >
          <RotateCcw className="w-3.5 h-3.5" /> {isIt ? "Nuova conversazione" : "New conversation"}
        </button>
      </div>

      <p className="text-[#8faa8f] text-xs mb-4">
        {isIt
          ? "Genera testi pronti all'uso: risposte a recensioni, email ai clienti, post social, descrizioni tour. Conosce già prezzi, orari e regole di Spirito Libero."
          : "Generate ready-to-use texts: review replies, customer emails, social posts, tour descriptions. It already knows Spirito Libero's prices, times and rules."}
      </p>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="ai-quick-actions">
        {quickActions.map((qa, i) => (
          <button
            key={i}
            data-testid={`ai-quick-${i}`}
            onClick={() => setInput(qa.prompt)}
            className="text-xs px-3 py-1.5 rounded-full glass-card text-[#c0d4c0] hover:border-[#d4a056]/60 hover:text-[#d4a056] transition-all border border-[#1a4a2e]"
          >
            {qa.label}
          </button>
        ))}
      </div>

      <div className="glass-card rounded-xl p-4 mb-4 min-h-[240px] max-h-[50vh] overflow-y-auto space-y-4" data-testid="ai-messages">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-[#5a7a5a] text-sm gap-2">
            <Bot className="w-8 h-8" />
            {isIt ? "Scrivi cosa ti serve o usa un'azione rapida." : "Type what you need or use a quick action."}
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} data-testid={`ai-msg-${i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] rounded-xl px-4 py-3 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#2d7a4a]/20 border border-[#2d7a4a]/40 text-[#e8f0e8]"
                  : "bg-[#0f2518] border border-[#d4a056]/30 text-[#c0d4c0]"
              }`}>
                {m.content || (streaming && i === messages.length - 1 ? (
                  <Loader2 className="w-4 h-4 animate-spin text-[#d4a056]" />
                ) : "")}
                {m.role === "assistant" && m.content && !(streaming && i === messages.length - 1) && (
                  <button
                    data-testid={`ai-copy-${i}`}
                    onClick={() => copyText(m.content)}
                    className="flex items-center gap-1 mt-2 text-[10px] uppercase font-bold text-[#8faa8f] hover:text-[#d4a056] transition-colors"
                  >
                    <Copy className="w-3 h-3" /> {isIt ? "Copia" : "Copy"}
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex gap-3 items-end">
        <textarea
          data-testid="ai-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
          }}
          rows={2}
          placeholder={isIt ? "Es: rispondi a questa recensione a 5 stelle..." : "E.g.: reply to this 5-star review..."}
          className="flex-1 bg-[#0f2518] border border-[#1a4a2e] text-[#e8f0e8] rounded-lg p-3 text-sm focus:border-[#d4a056] outline-none resize-none placeholder:text-[#3a5a3a]"
        />
        <button
          data-testid="ai-send-btn"
          onClick={sendMessage}
          disabled={streaming || !input.trim()}
          className="bg-[#d4a056] text-[#0a1a0f] font-bold uppercase px-5 py-3 text-xs hover:bg-[#e0b86e] transition-all disabled:opacity-50 rounded-lg flex items-center gap-2"
        >
          {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          {isIt ? "INVIA" : "SEND"}
        </button>
      </div>
    </div>
  );
}

/* ─── TOUR CONFIG TAB (Singolo / Doppio) ─── */
function TourConfigTab({ token, tourType }) {
  const { lang } = useLang();
  const isIt = lang === "it";
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const title = tourType === "single"
    ? (isIt ? "Tour Singolo" : "Single Tour")
    : (isIt ? "Tour Doppio" : "Double Tour");

  useEffect(() => {
    let cancelled = false;
    setCfg(null);
    axios.get(`${API}/tours/config`)
      .then((res) => {
        if (!cancelled && res?.data?.[tourType]) setCfg(res.data[tourType]);
      })
      .catch(() => toast.error(isIt ? "Errore caricamento configurazione" : "Error loading config"));
    return () => { cancelled = true; };
  }, [tourType]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/tours/config/${tourType}`, {
        description_it: cfg.description_it || "",
        description_en: cfg.description_en || "",
        price: parseFloat(cfg.price) || 0,
        max_quads: parseInt(cfg.max_quads) || 0,
      }, { headers });
      toast.success(isIt ? "Configurazione salvata" : "Configuration saved");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (isIt ? "Errore salvataggio" : "Save error"));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-[#2d7a4a] animate-spin" />
      </div>
    );
  }

  const fieldCls = "bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-10";

  return (
    <div className="max-w-3xl glass-card rounded-xl p-6" data-testid={`tourcfg-${tourType}`}>
      <div className="flex items-center gap-2 mb-5">
        {tourType === "single" ? <User className="w-4 h-4 text-[#2d7a4a]" /> : <Users className="w-4 h-4 text-[#2d7a4a]" />}
        <h3 className="font-bold uppercase text-sm tracking-widest text-[#2d7a4a]">
          {isIt ? `Personalizzazione ${title}` : `${title} customisation`}
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-5 max-w-sm">
        <div>
          <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Prezzo (€)" : "Price (€)"}</span>
          <Input
            data-testid={`tourcfg-${tourType}-price`}
            type="number" min="1" value={cfg.price}
            onChange={(e) => setCfg((c) => ({ ...c, price: e.target.value }))}
            className={fieldCls}
          />
        </div>
        <div>
          <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Quad disponibili" : "Available quads"}</span>
          <Input
            data-testid={`tourcfg-${tourType}-max-quads`}
            type="number" min="1" max="20" value={cfg.max_quads}
            onChange={(e) => setCfg((c) => ({ ...c, max_quads: e.target.value }))}
            className={fieldCls}
          />
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div>
          <span className="text-xs text-[#8faa8f] uppercase tracking-wider block mb-1.5">{isIt ? "Descrizione (Italiano)" : "Description (Italian)"}</span>
          <textarea
            data-testid={`tourcfg-${tourType}-desc-it`}
            value={cfg.description_it || ""}
            onChange={(e) => setCfg((c) => ({ ...c, description_it: e.target.value }))}
            rows={3} maxLength={255}
            className="w-full bg-[#0f2518] border border-[#1a4a2e] text-[#e8f0e8] rounded-lg p-3 text-sm focus:border-[#2d7a4a] outline-none resize-y"
          />
        </div>
        <div>
          <span className="text-xs text-[#8faa8f] uppercase tracking-wider block mb-1.5">{isIt ? "Descrizione (Inglese)" : "Description (English)"}</span>
          <textarea
            data-testid={`tourcfg-${tourType}-desc-en`}
            value={cfg.description_en || ""}
            onChange={(e) => setCfg((c) => ({ ...c, description_en: e.target.value }))}
            rows={3} maxLength={255}
            className="w-full bg-[#0f2518] border border-[#1a4a2e] text-[#e8f0e8] rounded-lg p-3 text-sm focus:border-[#2d7a4a] outline-none resize-y"
          />
        </div>
      </div>
      <button
        data-testid={`save-tourcfg-${tourType}-btn`}
        onClick={save}
        disabled={saving}
        className="bg-[#2d7a4a] text-white font-bold uppercase px-6 py-2.5 text-xs hover:bg-[#3a9960] transition-all disabled:opacity-50 rounded-lg"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isIt ? "SALVA MODIFICHE" : "SAVE CHANGES")}
      </button>
      <p className="text-[10px] text-[#5a7a5a] mt-3">
        {isIt
          ? "Prezzo e descrizione vengono aggiornati subito sul sito e nel checkout."
          : "Price and description are updated immediately on the website and checkout."}
      </p>
    </div>
  );
}

/* ─── GIORNI DI CHIUSURA (festività) ─── */
function ClosedDaysSection({ token }) {
  const { lang } = useLang();
  const isIt = lang === "it";
  const [days, setDays] = useState([]);
  const [newDay, setNewDay] = useState("");
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDays = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/closed-days`, { headers });
      setDays(Array.isArray(res.data) ? res.data : []);
    } catch { /* lista vuota */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchDays(); }, [fetchDays]);

  const closeDay = async () => {
    if (!newDay) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/close-day`, { date: newDay }, { headers });
      toast.success(isIt ? "Giornata chiusa alle prenotazioni" : "Day closed for bookings");
      setNewDay("");
      fetchDays();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (isIt ? "Errore chiusura" : "Close error"));
    } finally {
      setBusy(false);
    }
  };

  const openDay = async (d) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/close-day?date=${d}`, { headers });
      toast.success(isIt ? "Giornata riaperta" : "Day reopened");
      fetchDays();
    } catch {
      toast.error(isIt ? "Errore riapertura" : "Reopen error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass-card rounded-xl p-6 mb-8 border border-red-500/20" data-testid="closed-days-section">
      <div className="flex items-center gap-2 mb-2">
        <Ban className="w-4 h-4 text-red-400" />
        <h3 className="font-bold uppercase text-sm tracking-widest text-red-400">
          {isIt ? "Giorni di Chiusura Totale" : "Fully Closed Days"}
        </h3>
      </div>
      <p className="text-[#8faa8f] text-xs mb-4">
        {isIt
          ? "Seleziona le date (es. Natale, Pasqua) in cui NESSUN tour sarà prenotabile: tutti gli orari vengono chiusi."
          : "Select dates (e.g. Christmas, Easter) when NO tour can be booked: all time slots are closed."}
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <span className="text-[10px] text-[#8faa8f] uppercase block mb-1">{isIt ? "Data" : "Date"}</span>
          <Input
            data-testid="closed-day-input"
            type="date" value={newDay}
            onChange={(e) => setNewDay(e.target.value)}
            className="bg-[#0f2518] border-[#1a4a2e] text-[#e8f0e8] rounded-lg h-9 w-auto"
          />
        </div>
        <button
          data-testid="close-day-btn"
          onClick={closeDay}
          disabled={!newDay || busy}
          className="bg-red-500/80 text-white font-bold uppercase px-5 py-2 text-xs hover:bg-red-500 transition-all disabled:opacity-50 rounded-lg h-9"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (isIt ? "CHIUDI GIORNATA" : "CLOSE DAY")}
        </button>
      </div>
      {days.length === 0 ? (
        <p className="text-[#5a7a5a] text-xs" data-testid="no-closed-days">
          {isIt ? "Nessun giorno di chiusura impostato." : "No closed days set."}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {days.map((d) => (
            <span
              key={d}
              data-testid={`closed-day-${d}`}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-xs font-bold"
            >
              {d}
              <button
                data-testid={`reopen-day-${d}`}
                onClick={() => openDay(d)}
                disabled={busy}
                title={isIt ? "Riapri giornata" : "Reopen day"}
                className="text-red-300 hover:text-white transition-colors"
              >
                <XCircle className="w-3.5 h-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── STATISTICHE TAB ─── */
function StatsTab({ token }) {
  const { lang } = useLang();
  const isIt = lang === "it";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/admin/statistics`, { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => toast.error(isIt ? "Errore caricamento statistiche" : "Error loading statistics"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-[#2d7a4a] animate-spin" /></div>;
  }
  if (!data) {
    return <p className="text-[#8faa8f] text-sm" data-testid="stats-error">{isIt ? "Statistiche non disponibili." : "Statistics unavailable."}</p>;
  }

  const v = data.visitors || {};
  const bk = data.bookings || {};
  const f = data.funnel_30d || {};
  const rev = data.revenue || {};
  const daily = Array.isArray(data.daily_30d) ? data.daily_30d : [];
  const byTour = Array.isArray(data.by_tour) ? data.by_tour : [];
  const maxVisits = Math.max(1, ...daily.map((d) => d.visits));

  const Card = ({ label, value, sub, gold, testId }) => (
    <div className="glass-card rounded-xl p-4" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wider text-[#8faa8f] block mb-1">{label}</span>
      <span className={`text-2xl font-bold ${gold ? "text-[#d4a056]" : "text-[#e8f0e8]"}`}>{value}</span>
      {sub && <span className="text-[10px] text-[#5a7a5a] block mt-0.5">{sub}</span>}
    </div>
  );

  const FunnelRow = ({ label, value, pct, color }) => (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-[#c0d4c0]">{label}</span>
        <span className="text-[#e8f0e8] font-bold">{value}{pct !== undefined && <span className="text-[#d4a056] ml-2">{pct}%</span>}</span>
      </div>
      <div className="h-2 bg-[#0a1a0f] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(100, pct ?? 100)}%` }} />
      </div>
    </div>
  );

  const SectionTitle = ({ icon: Icon, children, gold }) => (
    <div className="flex items-center gap-2 mb-4 mt-8 first:mt-0">
      <Icon className={`w-4 h-4 ${gold ? "text-[#d4a056]" : "text-[#2d7a4a]"}`} />
      <h3 className={`font-bold uppercase text-sm tracking-widest ${gold ? "text-[#d4a056]" : "text-[#2d7a4a]"}`}>{children}</h3>
    </div>
  );

  return (
    <div data-testid="stats-tab">
      {data.tracking_since ? (
        <p className="text-[10px] text-[#5a7a5a] mb-4">
          {isIt ? `Conteggio visite attivo dal ${data.tracking_since}` : `Visit tracking active since ${data.tracking_since}`}
        </p>
      ) : (
        <p className="text-[10px] text-[#d4a056] mb-4" data-testid="stats-no-tracking">
          {isIt ? "Il conteggio visite parte da oggi: i numeri cresceranno con le prossime visite al sito." : "Visit tracking starts today: numbers will grow with upcoming site visits."}
        </p>
      )}

      <SectionTitle icon={Users}>{isIt ? "Visitatori" : "Visitors"}</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="stats-visitors">
        <Card label={isIt ? "Oggi" : "Today"} value={v.today ?? 0} testId="stat-visits-today" />
        <Card label={isIt ? "Ultima settimana" : "Last week"} value={v.week ?? 0} sub={`${v.new_week ?? 0} ${isIt ? "nuovi" : "new"}`} testId="stat-visits-week" />
        <Card label={isIt ? "Ultimo mese" : "Last month"} value={v.month ?? 0} sub={`${v.new_month ?? 0} ${isIt ? "nuovi" : "new"}`} testId="stat-visits-month" />
        <Card label={isIt ? "Ultimo anno" : "Last year"} value={v.year ?? 0} testId="stat-visits-year" />
        <Card label={isIt ? "Totali" : "Total"} value={v.total ?? 0} sub={`${v.new_total ?? 0} ${isIt ? "visitatori unici" : "unique visitors"}`} gold testId="stat-visits-total" />
      </div>

      <SectionTitle icon={TrendingUp} gold>{isIt ? "Funnel di Conversione (ultimi 30 giorni)" : "Conversion Funnel (last 30 days)"}</SectionTitle>
      <div className="glass-card rounded-xl p-5 max-w-2xl" data-testid="stats-funnel">
        <FunnelRow label={isIt ? "Visite al sito" : "Site visits"} value={f.visits ?? 0} color="bg-[#2d7a4a]" />
        <FunnelRow label={isIt ? "Aperta pagina prenotazione" : "Opened booking page"} value={f.booking_page ?? 0} pct={f.visit_to_page_pct ?? 0} color="bg-[#3a9960]" />
        <FunnelRow label={isIt ? "Checkout avviati" : "Checkouts started"} value={f.checkouts ?? 0} pct={f.page_to_checkout_pct ?? 0} color="bg-[#d4a056]" />
        <FunnelRow label={isIt ? "Prenotazioni pagate" : "Paid bookings"} value={f.paid ?? 0} pct={f.checkout_to_paid_pct ?? 0} color="bg-[#e0b86e]" />
        <p className="text-xs text-[#8faa8f] mt-3">
          {isIt ? "Conversione complessiva visita → prenotazione pagata: " : "Overall visit → paid booking conversion: "}
          <span className="text-[#d4a056] font-bold" data-testid="stat-overall-conversion">{f.visit_to_paid_pct ?? 0}%</span>
        </p>
      </div>

      <SectionTitle icon={CalendarDays}>{isIt ? "Prenotazioni" : "Bookings"}</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-bookings">
        <Card label={isIt ? "Ultima settimana" : "Last week"} value={bk.week ?? 0} sub={`${bk.confirmed_week ?? 0} ${isIt ? "pagate" : "paid"}`} testId="stat-bookings-week" />
        <Card label={isIt ? "Ultimo mese" : "Last month"} value={bk.month ?? 0} sub={`${bk.confirmed_month ?? 0} ${isIt ? "pagate" : "paid"}`} testId="stat-bookings-month" />
        <Card label={isIt ? "Ultimo anno" : "Last year"} value={bk.year ?? 0} testId="stat-bookings-year" />
        <Card label={isIt ? "Totali confermate" : "Total confirmed"} value={bk.confirmed ?? 0} sub={`${isIt ? "su" : "of"} ${bk.total ?? 0} (${bk.paid_rate ?? 0}%)`} gold testId="stat-bookings-confirmed" />
        <Card label={isIt ? "Valore medio" : "Avg value"} value={`€${bk.avg_value ?? 0}`} sub={`${bk.avg_quads ?? 0} quad ${isIt ? "in media" : "avg"}`} testId="stat-avg-value" />
        <Card label={isIt ? "Tasso cancellazioni" : "Cancel rate"} value={`${bk.cancel_rate ?? 0}%`} sub={`${bk.cancelled ?? 0} ${isIt ? "cancellate" : "cancelled"}`} testId="stat-cancel-rate" />
      </div>

      <SectionTitle icon={DollarSign} gold>{isIt ? "Guadagni" : "Earnings"}</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl" data-testid="stats-revenue">
        {[
          { k: "month", label: isIt ? "Ultimo mese" : "Last month" },
          { k: "year", label: isIt ? "Ultimo anno" : "Last year" },
          { k: "total", label: isIt ? "Totale" : "Total" },
        ].map(({ k, label }) => (
          <div key={k} className="glass-card rounded-xl p-4" data-testid={`stat-revenue-${k}`}>
            <span className="text-[10px] uppercase tracking-wider text-[#8faa8f] block mb-2">{label}</span>
            <span className="text-2xl font-bold text-[#d4a056] block">€{(rev[k]?.gross ?? 0).toFixed(0)}</span>
            <span className="text-[10px] text-[#8faa8f] block mt-1">
              {isIt ? "Fee 10%" : "10% fee"}: €{(rev[k]?.management_fee ?? 0).toFixed(0)} · {isIt ? "Netto" : "Net"}: <span className="text-[#2d7a4a] font-bold">€{(rev[k]?.net_amount ?? 0).toFixed(0)}</span>
            </span>
          </div>
        ))}
      </div>

      {byTour.length > 0 && (
        <>
          <SectionTitle icon={Mountain}>{isIt ? "Per Tour (confermate)" : "By Tour (confirmed)"}</SectionTitle>
          <div className="glass-card rounded-xl p-5 max-w-2xl" data-testid="stats-by-tour">
            {byTour.map((t) => (
              <div key={t.tour} className="flex justify-between items-center py-2 border-b border-[#1a2e20] last:border-0 text-sm">
                <span className="text-[#c0d4c0]">{t.tour}</span>
                <span className="text-[#e8f0e8]"><span className="text-[#8faa8f] text-xs mr-3">{t.count} {isIt ? "pren." : "bkg."}</span><span className="text-[#d4a056] font-bold">€{t.revenue.toFixed(0)}</span></span>
              </div>
            ))}
          </div>
        </>
      )}

      <SectionTitle icon={BarChart3}>{isIt ? "Andamento ultimi 30 giorni" : "Last 30 days trend"}</SectionTitle>
      <div className="glass-card rounded-xl p-5" data-testid="stats-daily-chart">
        <div className="flex items-end gap-[2px] h-28">
          {daily.map((d) => (
            <div key={d.date} className="flex-1 flex flex-col justify-end items-center gap-[2px]" title={`${d.date}: ${d.visits} ${isIt ? "visite" : "visits"}, ${d.bookings} ${isIt ? "prenotazioni" : "bookings"}`}>
              {d.confirmed > 0 && <div className="w-full max-w-[10px] bg-[#d4a056] rounded-sm" style={{ height: `${Math.min(100, d.confirmed * 14)}%` }} />}
              <div className="w-full max-w-[10px] bg-[#2d7a4a]/70 rounded-sm" style={{ height: `${Math.max(2, (d.visits / maxVisits) * 70)}%` }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[9px] text-[#5a7a5a] mt-2">
          <span>{daily[0]?.date}</span>
          <span className="flex gap-3">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#2d7a4a]/70 rounded-sm inline-block" /> {isIt ? "Visite" : "Visits"}</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#d4a056] rounded-sm inline-block" /> {isIt ? "Pren. pagate" : "Paid bkg."}</span>
          </span>
          <span>{daily[daily.length - 1]?.date}</span>
        </div>
      </div>
    </div>
  );
}

/* ─── MAIN ADMIN PAGE ─── */
export default function AdminPage() {
  const { t } = useLang();
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [activeTab, setActiveTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [verified, setVerified] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const verify = async (attempt = 0) => {
      try {
        const res = await axios.get(`${API}/admin/stats`, { headers, timeout: 45000 });
        if (cancelled) return;
        setStats(res.data);
        setVerified(true);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          localStorage.removeItem("admin_token");
          setToken("");
        } else if (attempt < 2) {
          setTimeout(() => verify(attempt + 1), 5000);
        } else {
          setVerified(true);
        }
      }
    };
    verify();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setVerified(false);
  };

  if (!token || !verified) {
    return <LoginScreen onLogin={(t) => setToken(t)} />;
  }

  return (
    <div className="min-h-screen bg-[#0a1a0f] text-[#e8f0e8]">
      <header className="border-b border-[#1a4a2e] px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mountain className="w-4 h-4 text-[#2d7a4a]" />
            <span className="font-bold text-[#e8f0e8] uppercase text-sm tracking-wider">
              {t("adminTitle")}
            </span>
          </div>
          <button
            data-testid="admin-logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 text-[#8faa8f] hover:text-[#d4a056] transition-colors text-sm"
          >
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8" data-testid="admin-stats">
            <StatCard icon={Users} label={t("statsTotal")} value={stats.total} />
            <StatCard icon={DollarSign} label={t("statsGross")} value={`€${(stats.gross_revenue || 0).toFixed(0)}`} gold />
            <StatCard icon={Percent} label={t("statsFee")} value={`€${(stats.management_fee_total || 0).toFixed(0)}`} />
            <StatCard icon={TrendingUp} label={t("statsNet")} value={`€${(stats.net_revenue || 0).toFixed(0)}`} accent />
            <StatCard icon={CheckCircle} label={t("statsConfirmed")} value={stats.confirmed} />
            <StatCard icon={AlertCircle} label={t("statsToday")} value={stats.today} />
          </div>
        )}

        <ReportBar token={token} />

        <div className="flex gap-1 mb-8 border-b border-[#1a4a2e]">
          <button
            data-testid="tab-bookings"
            onClick={() => setActiveTab("bookings")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${
              activeTab === "bookings"
                ? "border-[#2d7a4a] text-[#2d7a4a]"
                : "border-transparent text-[#8faa8f] hover:text-[#e8f0e8]"
            }`}
          >
            <List className="w-4 h-4" /> {t("tabBookings")}
          </button>
          <button
            data-testid="tab-slots"
            onClick={() => setActiveTab("slots")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${
              activeTab === "slots"
                ? "border-[#2d7a4a] text-[#2d7a4a]"
                : "border-transparent text-[#8faa8f] hover:text-[#e8f0e8]"
            }`}
          >
            <Settings className="w-4 h-4" /> {t("tabSlots")}
          </button>
          <button
            data-testid="tab-tour-single"
            onClick={() => setActiveTab("tour_single")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${
              activeTab === "tour_single"
                ? "border-[#2d7a4a] text-[#2d7a4a]"
                : "border-transparent text-[#8faa8f] hover:text-[#e8f0e8]"
            }`}
          >
            <User className="w-4 h-4" /> {t("tourSingleName")}
          </button>
          <button
            data-testid="tab-tour-double"
            onClick={() => setActiveTab("tour_double")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${
              activeTab === "tour_double"
                ? "border-[#2d7a4a] text-[#2d7a4a]"
                : "border-transparent text-[#8faa8f] hover:text-[#e8f0e8]"
            }`}
          >
            <Users className="w-4 h-4" /> {t("tourDoubleName")}
          </button>
          <button
            data-testid="tab-mottarone"
            onClick={() => setActiveTab("mottarone")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${
              activeTab === "mottarone"
                ? "border-[#d4a056] text-[#d4a056]"
                : "border-transparent text-[#8faa8f] hover:text-[#e8f0e8]"
            }`}
          >
            <Sparkles className="w-4 h-4" /> {t("tabMottarone")}
          </button>
          <button
            data-testid="tab-ai"
            onClick={() => setActiveTab("ai")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${
              activeTab === "ai"
                ? "border-[#d4a056] text-[#d4a056]"
                : "border-transparent text-[#8faa8f] hover:text-[#e8f0e8]"
            }`}
          >
            <Bot className="w-4 h-4" /> AI
          </button>
          <button
            data-testid="tab-stats"
            onClick={() => setActiveTab("stats")}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-bold uppercase tracking-wider transition-colors border-b-2 -mb-[1px] ${
              activeTab === "stats"
                ? "border-[#d4a056] text-[#d4a056]"
                : "border-transparent text-[#8faa8f] hover:text-[#e8f0e8]"
            }`}
          >
            <BarChart3 className="w-4 h-4" /> {t("tabStats")}
          </button>
        </div>

        {activeTab === "bookings" && <BookingsTab token={token} />}
        {activeTab === "slots" && (
          <>
            <ClosedDaysSection token={token} />
            <SlotsTab token={token} />
          </>
        )}
        {activeTab === "tour_single" && <TourConfigTab token={token} tourType="single" />}
        {activeTab === "tour_double" && <TourConfigTab token={token} tourType="double" />}
        {activeTab === "mottarone" && <MottaroneTab token={token} />}
        {activeTab === "ai" && <AiTab token={token} />}
        {activeTab === "stats" && <StatsTab token={token} />}
      </main>
    </div>
  );
}
